import { Address } from "@modules/address/addressTypes"
import { BenchmarkTypeOf } from "@modules/benchmark/benchmarkTypes"
import { BoxTypeOf } from "@modules/box/boxTypes"
import { Chrono } from "@modules/chrono/chronoTypes"
import { convertChronoFromDto } from "@modules/chrono/services/chronoDtos"
import { convertImageFromDto } from "@modules/dtos/imageDto"
import { ExploreContentType } from "@modules/explore/exploreTypes"
import {
  ExploreBenchmarkItemDto,
  ExploreBoxItemDto,
  ExploreContentDto,
  ExploreProfileItemDto,
  ExploreProgramBundleItemDto,
  ExploreProgramItemDto,
} from "@modules/explore/gateway/exploreDto"
import { convertUserFromDto } from "@modules/profile/profileDtos"
import { ProfileTypeOf, User } from "@modules/profile/profileTypes"
import { ProgramBundleType, ProgramBundleTypeOf } from "@modules/programBundle/api/programBundleDto"
import { ProgramInfinite, ProgramInfiniteTypeOf } from "@modules/programs/programInfiniteTypes"
import { ProgramOnOff, ProgramOnOffTypeOf } from "@modules/programs/programOnOffTypes"
import {
  ProgramContentTypeOf,
  ProgramPrice,
  ProgramType,
  ProgramTypeOf,
  ProgramVisibility,
} from "@modules/programs/programTypes"
import { HUImage } from "@modules/types/imageTypes"

export class ExploreContentEntity {
  id: string
  type: ExploreContentType
  benchmark?: ExploreBenchmarkItemEntity
  program?: ExploreProgramItemEntity | ExploreProgramBundleItemEntity
  box?: ExploreBoxItemEntity
  profile?: ExploreProfileItemEntity

  constructor(globalExploreContentEntity: ExploreContentEntity) {
    this.id = globalExploreContentEntity.id
    this.type = globalExploreContentEntity.type
    this.benchmark = globalExploreContentEntity.benchmark
    this.program = globalExploreContentEntity.program
    this.box = globalExploreContentEntity.box
    this.profile = globalExploreContentEntity.profile
  }

  static fromDto(dto: ExploreContentDto): ExploreContentEntity {
    switch (dto.contentType) {
      case ProgramContentTypeOf:
        return new ExploreProgramContentEntity({
          id: dto.content.id,
          type: ProgramContentTypeOf,
          program: ExploreProgramItemEntity.fromDto(dto.content),
        })
      case ProgramBundleTypeOf:
        return new ExploreProgramBundleContentEntity({
          id: dto.content.id,
          type: ProgramBundleTypeOf,
          program: ExploreProgramBundleItemEntity.fromDto(dto.content),
        })
      case BenchmarkTypeOf:
        return new ExploreBenchmarkContentEntity({
          id: dto.content.id,
          type: BenchmarkTypeOf,
          benchmark: ExploreBenchmarkItemEntity.fromDto(dto.content),
        })

      case BoxTypeOf:
        return new ExploreBoxContentEntity({
          id: dto.content.id,
          type: BoxTypeOf,
          box: ExploreBoxItemEntity.fromDto(dto.content),
        })
      case ProfileTypeOf:
        return new ExploreContentEntity({
          id: dto.content.id,
          type: ProfileTypeOf,
          profile: ExploreProfileItemEntity.fromDto(dto.content),
        })
    }
  }
}

export class ExploreProgramContentEntity extends ExploreContentEntity {
  program: ExploreProgramItemEntity

  constructor(exploreProgramContent: ExploreProgramContentEntity) {
    super(exploreProgramContent)
    this.program = exploreProgramContent.program
  }
}

export class ExploreProgramBundleContentEntity extends ExploreContentEntity {
  program: ExploreProgramBundleItemEntity

  constructor(exploreProgramContent: ExploreProgramBundleContentEntity) {
    super(exploreProgramContent)
    this.program = exploreProgramContent.program
  }
}
export class ExploreBenchmarkContentEntity extends ExploreContentEntity {
  benchmark: ExploreBenchmarkItemEntity

  constructor(exploreBenchmarkContent: ExploreBenchmarkContentEntity) {
    super(exploreBenchmarkContent)
    this.benchmark = exploreBenchmarkContent.benchmark
  }
}
export class ExploreBoxContentEntity extends ExploreContentEntity {
  box: ExploreBoxItemEntity

  constructor(exploreBoxContent: ExploreBoxContentEntity) {
    super(exploreBoxContent)
    this.box = exploreBoxContent.box
  }
}
export class ExploreProfileContentEntity extends ExploreContentEntity {
  profile: ExploreProfileItemEntity

  constructor(exploreProfileContent: ExploreProfileContentEntity) {
    super(exploreProfileContent)
    this.profile = exploreProfileContent.profile
  }
}

export class ExploreBenchmarkItemEntity {
  id: string
  title: string
  category: string
  chrono?: Chrono
  isFavorite: boolean
  rpe?: number
  _type: typeof BenchmarkTypeOf

  constructor(benchmarkItem: ExploreBenchmarkItemEntity) {
    this.id = benchmarkItem.id
    this._type = BenchmarkTypeOf
    this.title = benchmarkItem.title
    this.category = benchmarkItem.category
    this.chrono = benchmarkItem.chrono
    this.isFavorite = benchmarkItem.isFavorite
    this.rpe = benchmarkItem.rpe
  }

  static fromDto(dto: ExploreBenchmarkItemDto): ExploreBenchmarkItemEntity {
    return new ExploreBenchmarkItemEntity({
      id: dto.id,
      _type: BenchmarkTypeOf,
      title: dto.title,
      category: dto.category,
      chrono: dto.chrono ? convertChronoFromDto(dto.chrono) : undefined,
      isFavorite: dto.isFavorite,
      rpe: dto.rpe ?? undefined,
    })
  }
}

export class ExploreProfileItemEntity {
  id: string
  _type: typeof ProfileTypeOf
  firstname: string
  lastname: string
  username: string
  avatar?: HUImage

  constructor(profileItem: ExploreProfileItemEntity) {
    this.id = profileItem.id
    this._type = ProfileTypeOf
    this.firstname = profileItem.firstname
    this.lastname = profileItem.lastname
    this.username = profileItem.username
    this.avatar = profileItem.avatar
  }

  static fromDto(dto: ExploreProfileItemDto): ExploreProfileItemEntity {
    return new ExploreProfileItemEntity({
      id: dto.id,
      _type: ProfileTypeOf,
      firstname: dto.firstname,
      lastname: dto.lastname,
      username: dto.username,
      avatar: dto.avatar ? convertImageFromDto(dto.avatar) : undefined,
    })
  }
}

export class ExploreBoxItemEntity {
  id: string
  _type: typeof BoxTypeOf
  name: string
  address: Address
  logo: HUImage
  cover: HUImage
  isSubscribed: boolean
  event?: boolean

  constructor(boxItem: ExploreBoxItemEntity) {
    this.id = boxItem.id
    this._type = BoxTypeOf
    this.name = boxItem.name
    this.address = boxItem.address
    this.logo = boxItem.logo
    this.cover = boxItem.cover
    this.isSubscribed = boxItem.isSubscribed
    this.event = boxItem.event
  }

  static fromDto(dto: ExploreBoxItemDto): ExploreBoxItemEntity {
    return new ExploreBoxItemEntity({
      id: dto.id,
      _type: BoxTypeOf,
      name: dto.name,
      logo: convertImageFromDto(dto.logo),
      cover: convertImageFromDto(dto.cover),
      address: dto.address,
      isSubscribed: dto.isSubscribed,
      event: dto.event ?? undefined,
    })
  }
}

export class ExploreProgramItemEntity {
  id: string
  name: string
  image?: HUImage
  owner: User
  visibility: ProgramVisibility
  subscribed: boolean
  subscriptionCount: number
  price: ProgramPrice | null
  defaultHideSessionContent: boolean
  _type: typeof ProgramContentTypeOf
  programType: ProgramTypeOf
  exploreType: typeof ProgramContentTypeOf

  constructor(programItem: ExploreProgramItemEntity) {
    this.id = programItem.id
    this.name = programItem.name
    this.image = programItem.image
    this.owner = programItem.owner
    this.visibility = programItem.visibility
    this.subscribed = programItem.subscribed
    this.subscriptionCount = programItem.subscriptionCount
    this.price = programItem.price
    this.defaultHideSessionContent = programItem.defaultHideSessionContent
    this.programType = programItem.programType
    this.exploreType = programItem.exploreType
    this._type = ProgramContentTypeOf
  }

  static fromDto(dto: ExploreProgramItemDto): ExploreProgramItemEntity {
    return new ExploreProgramItemEntity({
      id: dto.id,
      name: dto.name,
      image: dto.image ? convertImageFromDto(dto.image) : undefined,
      owner: convertUserFromDto(dto.owner),
      visibility: dto.visibility,
      subscribed: dto.subscribed,
      subscriptionCount: dto.subscriptionCount,
      price: dto.price ?? null,
      defaultHideSessionContent: dto.defaultHideSessionContent,
      programType: dto.type === ProgramType.VIRTUAL ? ProgramInfiniteTypeOf : ProgramOnOffTypeOf,
      exploreType: dto.exploreType,
      _type: ProgramContentTypeOf,
    })
  }

  static fromProgram(program: ProgramOnOff | ProgramInfinite): ExploreProgramItemEntity {
    return new ExploreProgramItemEntity({
      ...program,
      owner: program.author,
      name: program.title,
      programType: program._programType,
      exploreType: ProgramContentTypeOf,
      subscribed: program.subscribed ?? false,
    })
  }
}

export class ExploreProgramBundleItemEntity {
  id: string
  name: string
  image?: HUImage
  owner: User
  visibility: ProgramVisibility
  subscribed: boolean
  subscriptionCount: number
  price?: ProgramPrice
  nbPrograms: number
  _type: typeof ProgramBundleTypeOf
  programType: ProgramTypeOf
  exploreType: typeof ProgramBundleTypeOf

  constructor(programItem: ExploreProgramBundleItemEntity) {
    this.id = programItem.id
    this.name = programItem.name
    this.image = programItem.image
    this.owner = programItem.owner
    this.visibility = programItem.visibility
    this.subscribed = programItem.subscribed
    this.subscriptionCount = programItem.subscriptionCount
    this.price = programItem.price
    this.nbPrograms = programItem.nbPrograms
    this.programType = programItem.programType
    this.exploreType = programItem.exploreType
    this._type = ProgramBundleTypeOf
  }

  static fromDto(dto: ExploreProgramBundleItemDto): ExploreProgramBundleItemEntity {
    return new ExploreProgramBundleItemEntity({
      id: dto.id,
      name: dto.name,
      image: dto.image ? convertImageFromDto(dto.image) : undefined,
      owner: convertUserFromDto(dto.owner),
      visibility: dto.visibility,
      subscribed: dto.subscribed,
      subscriptionCount: dto.subscriptionCount,
      price: dto.price ?? undefined,
      programType: dto.type === ProgramBundleType.VIRTUAL ? ProgramInfiniteTypeOf : ProgramOnOffTypeOf,
      exploreType: dto.exploreType,
      nbPrograms: dto.nbPrograms,
      _type: ProgramBundleTypeOf,
    })
  }
}
